import classNames from 'classnames';
import { observer } from 'mobx-react';

import Icon from '../../../../app/shared/components/Icon/Icon';
import Button from '../../../ui/Button/Button';
import LoadingStatus from '../../../ui/LoadingStatus/LoadingStatus';
import Markdown from '../../../ui/Markdown/Markdown';
import { Modal, ModalBody, ModalContent, ModalHeader, ModalHeading } from '../../../ui/Modal/Modal';
import Skeleton from '../../../ui/Skeleton/Skeleton';
import SkeletonText from '../../../ui/SkeletonText/SkeletonText';
import css from './TermsModal.css';
import TermsModalState from './TermsModalState';

interface TermsModalProps {
  state: TermsModalState;
}

const TermsModal = observer(({ state }: TermsModalProps) => {
  const { brand, paragraphs, translation } = state;

  return (
    <Modal isOpen={state.visible} onOpenChange={state.toggle}>
      <Button theme="primary" variant="ghost">
        <Icon name="doc" className={css.icon} /> {translation.triggerLabel}
      </Button>
      <ModalContent>
        <ModalHeader className={css.header} closeLabel={translation.closeLabel}>
          <ModalHeading>{translation.heading}</ModalHeading>
          <p className={css.intro}>{translation.introFormat.interpolate([['brand', brand.name]])}</p>
        </ModalHeader>
        <ModalBody className={classNames(css.body, { [css.withLoader]: state.isLoading })}>
          {state.isLoading ? (
            <>
              <LoadingStatus message={translation.loadingLabel} />
              <TermsSkeleton />
            </>
          ) : (
            paragraphs.map((paragraph) => (
              <section className={css.paragraph} key={paragraph.id}>
                <Markdown content={paragraph.content} />
              </section>
            ))
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
});

export default TermsModal;

const TermsSkeleton = () => {
  return (
    <div className={css.skeleton}>
      <Skeleton className={css.skeletonTitle} />
      <SkeletonText className={css.skeletonParagraph} lineCount={4} />
      <Skeleton className={css.skeletonTitle} />
      <SkeletonText className={css.skeletonParagraph} lineCount={6} />
      <Skeleton className={css.skeletonTitle} />
      <SkeletonText className={css.skeletonParagraph} lineCount={5} />
      <Skeleton className={css.skeletonTitle} />
      <SkeletonText className={css.skeletonParagraph} lineCount={8} />
    </div>
  );
};
